<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const isLoading = ref(true);
const hasLicenseError = ref(false);
const data = ref({ metrics: {}, inboxes: [], calls: [] });

const endpoint = computed(
  () => `/api/v1/accounts/${route.params.accountId}/voice_dashboard`
);

const formatDuration = seconds => {
  const value = Number(seconds || 0);
  const minutes = Math.floor(value / 60);
  return `${minutes}:${String(value % 60).padStart(2, '0')}`;
};

const metrics = computed(() => [
  {
    label: t('VOICE_OS.METRICS.TOTAL'),
    value: data.value.metrics.total || 0,
    detail: t('VOICE_OS.METRICS.LAST_30_DAYS'),
  },
  {
    label: t('VOICE_OS.METRICS.ACTIVE'),
    value: data.value.metrics.active || 0,
    detail: t('VOICE_OS.METRICS.NOW'),
  },
  {
    label: t('VOICE_OS.METRICS.ANSWER_RATE'),
    value: `${data.value.metrics.answer_rate || 0}%`,
    detail: t('VOICE_OS.METRICS.ANSWERED', {
      count: data.value.metrics.answered || 0,
    }),
  },
  {
    label: t('VOICE_OS.METRICS.AVG_DURATION'),
    value: formatDuration(data.value.metrics.average_duration),
    detail: t('VOICE_OS.METRICS.MISSED', {
      count: data.value.metrics.missed || 0,
    }),
  },
]);

const load = async () => {
  isLoading.value = true;
  try {
    const response = await window.axios.get(endpoint.value);
    data.value = response.data;
  } catch (error) {
    if (
      error.response?.status === 403 ||
      error.response?.data?.code === 'STACKLAB_TOKEN_NOT_FOUND' ||
      error.data?.code === 'STACKLAB_TOKEN_NOT_FOUND'
    ) {
      hasLicenseError.value = true;
      return;
    }
    useAlert(t('VOICE_OS.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const formatDate = value =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));

const statusLabel = status => {
  if (status === 'in_progress') return t('VOICE_OS.STATUS.IN_PROGRESS');
  if (status === 'completed') return t('VOICE_OS.STATUS.COMPLETED');
  if (status === 'no_answer') return t('VOICE_OS.STATUS.NO_ANSWER');
  if (status === 'failed') return t('VOICE_OS.STATUS.FAILED');
  return t('VOICE_OS.STATUS.RINGING');
};

const providerIcon = provider => {
  if (provider === 'twilio') return 'i-lucide-phone-call';
  return 'i-ri-whatsapp-fill';
};

const callContext = call =>
  t('VOICE_OS.HISTORY.CONTEXT', {
    inbox: call.inbox.name,
    phone: call.contact.phone_number || t('VOICE_OS.HISTORY.NO_PHONE'),
  });

const openConversation = call =>
  router.push({
    name: 'conversation_through_inbox',
    params: {
      accountId: route.params.accountId,
      inbox_id: call.inbox.id,
      conversation_id: call.conversation_id,
    },
  });

onMounted(load);
</script>

<template>
  <main
    v-if="!hasLicenseError"
    class="flex-1 overflow-y-auto bg-n-background p-6 lg:p-8"
  >
    <div class="mx-auto flex w-full max-w-6xl flex-col gap-7">
      <header
        class="flex flex-col justify-between gap-4 sm:flex-row sm:items-start"
      >
        <div>
          <h1 class="text-2xl font-semibold text-n-slate-12">
            {{ t('VOICE_OS.TITLE') }}
          </h1>
          <p class="mt-1 max-w-2xl text-sm text-n-slate-11">
            {{ t('VOICE_OS.DESCRIPTION') }}
          </p>
        </div>
        <div class="flex gap-2">
          <Button
            color="slate"
            variant="outline"
            icon="i-lucide-refresh-cw"
            :label="t('VOICE_OS.REFRESH')"
            @click="load"
          />
          <Button
            icon="i-lucide-phone-outgoing"
            :label="t('VOICE_OS.NEW_CALL')"
            @click="router.push({ name: 'contacts_dashboard_index' })"
          />
        </div>
      </header>

      <div v-if="isLoading" class="flex min-h-80 items-center justify-center">
        <Spinner />
      </div>

      <template v-else>
        <section
          class="grid overflow-hidden rounded-xl border border-n-weak bg-n-solid-2 sm:grid-cols-2 lg:grid-cols-4"
        >
          <div
            v-for="(metric, index) in metrics"
            :key="metric.label"
            class="p-5"
            :class="
              index ? 'border-t border-n-weak sm:border-s lg:border-t-0' : ''
            "
          >
            <p class="text-sm text-n-slate-11">{{ metric.label }}</p>
            <p class="mt-1 text-2xl font-semibold tabular-nums text-n-slate-12">
              {{ metric.value }}
            </p>
            <p class="mt-1 text-xs text-n-slate-10">{{ metric.detail }}</p>
          </div>
        </section>

        <section class="flex flex-col gap-3">
          <div class="flex items-end justify-between gap-4">
            <div>
              <h2 class="text-base font-medium text-n-slate-12">
                {{ t('VOICE_OS.INBOXES.TITLE') }}
              </h2>
              <p class="text-sm text-n-slate-11">
                {{ t('VOICE_OS.INBOXES.DESCRIPTION') }}
              </p>
            </div>
            <span class="text-sm text-n-slate-10">
              {{ t('VOICE_OS.INBOXES.COUNT', { count: data.inboxes.length }) }}
            </span>
          </div>

          <div
            v-if="data.inboxes.length"
            class="divide-y divide-n-weak overflow-hidden rounded-xl border border-n-weak bg-n-solid-2"
          >
            <div
              v-for="inbox in data.inboxes"
              :key="inbox.id"
              class="flex items-center gap-3 p-4"
            >
              <span
                :class="providerIcon(inbox.provider)"
                class="size-5 text-n-slate-11"
              />
              <div class="min-w-0 flex-1">
                <p class="truncate text-sm font-medium text-n-slate-12">
                  {{ inbox.name }}
                </p>
                <p class="text-xs text-n-slate-10">
                  {{
                    t('VOICE_OS.INBOXES.PROVIDER', { provider: inbox.provider })
                  }}
                </p>
              </div>
              <div class="hidden text-right sm:block">
                <p class="text-sm font-medium text-n-slate-12">
                  {{ inbox.calls_30d }}
                </p>
                <p class="text-xs text-n-slate-10">
                  {{ t('VOICE_OS.INBOXES.CALLS') }}
                </p>
              </div>
              <span
                class="inline-flex items-center gap-1.5 text-xs font-medium text-n-teal-11"
              >
                <span class="size-2 rounded-full bg-n-teal-9" />
                {{ t('VOICE_OS.INBOXES.READY') }}
              </span>
            </div>
          </div>
          <div
            v-else
            class="rounded-xl border border-n-weak bg-n-solid-2 p-8 text-center"
          >
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('VOICE_OS.INBOXES.EMPTY_TITLE') }}
            </p>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ t('VOICE_OS.INBOXES.EMPTY_DESCRIPTION') }}
            </p>
          </div>
        </section>

        <section class="flex flex-col gap-3">
          <div class="flex items-end justify-between gap-4">
            <div>
              <h2 class="text-base font-medium text-n-slate-12">
                {{ t('VOICE_OS.HISTORY.TITLE') }}
              </h2>
              <p class="text-sm text-n-slate-11">
                {{ t('VOICE_OS.HISTORY.DESCRIPTION') }}
              </p>
            </div>
            <span class="text-sm text-n-slate-10">
              {{
                t('VOICE_OS.HISTORY.ARTIFACTS', {
                  recordings: data.metrics.recordings || 0,
                  transcripts: data.metrics.transcripts || 0,
                })
              }}
            </span>
          </div>

          <div
            v-if="data.calls.length"
            class="divide-y divide-n-weak overflow-hidden rounded-xl border border-n-weak bg-n-solid-2"
          >
            <button
              v-for="call in data.calls"
              :key="call.id"
              type="button"
              class="flex w-full items-center gap-3 p-4 text-start hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
              @click="openConversation(call)"
            >
              <Avatar :name="call.contact.name" :size="40" />
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <p class="truncate text-sm font-medium text-n-slate-12">
                    {{ call.contact.name }}
                  </p>
                  <span
                    :class="providerIcon(call.provider)"
                    class="size-3.5 text-n-slate-10"
                  />
                </div>
                <p class="truncate text-xs text-n-slate-10">
                  {{ callContext(call) }}
                </p>
              </div>
              <div class="hidden min-w-28 text-right md:block">
                <p class="text-sm text-n-slate-12">
                  {{ statusLabel(call.status) }}
                </p>
                <p class="text-xs text-n-slate-10">
                  {{ formatDate(call.created_at) }}
                </p>
              </div>
              <div
                class="flex min-w-20 items-center justify-end gap-2 text-n-slate-10"
              >
                <span
                  v-if="call.recording_url"
                  class="i-lucide-file-audio size-4"
                />
                <span
                  v-if="call.transcript"
                  class="i-lucide-file-text size-4"
                />
                <span class="text-sm tabular-nums">{{
                  formatDuration(call.duration_seconds)
                }}</span>
              </div>
            </button>
          </div>
          <div
            v-else
            class="rounded-xl border border-n-weak bg-n-solid-2 p-8 text-center"
          >
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('VOICE_OS.HISTORY.EMPTY_TITLE') }}
            </p>
            <p class="mt-1 text-sm text-n-slate-11">
              {{ t('VOICE_OS.HISTORY.EMPTY_DESCRIPTION') }}
            </p>
          </div>
        </section>
      </template>
    </div>
  </main>

  <div
    v-else
    class="flex h-full w-full items-center justify-center bg-n-surface-2"
  >
    <div class="mx-auto max-w-md p-6 text-center">
      <span class="i-lucide-lock mb-4 inline-block size-12 text-n-slate-10" />
      <h2 class="mb-2 text-xl font-semibold text-n-slate-12">
        {{ t('VOICE_OS.LICENSE_REQUIRED') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ t('VOICE_OS.LICENSE_DESCRIPTION') }}
      </p>
    </div>
  </div>
</template>
